/** `/admin/tracks/:officialId` — one circuit's history.
 *
 * Everything that ever happened to a circuit, in order: the surveys that
 * arrived and who sent them, what was decided about each and by whom, the
 * pull requests the job opened and how they settled, the issues raised and
 * how they were answered, the publications, the edits. The public circuit
 * page shows the merges; this shows the work behind them, which is what an
 * administrator wants when a circuit looks wrong and the question is "what
 * happened here, and when".
 *
 * The header is the circuit as the Tracks page knows it — status, coverage,
 * when it was last published — with the ways out: its public page, the
 * editor, its uploads. The feed under it is the same component as the Logs
 * screen's, narrowed to the circuit.
 */

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { ActivityFeed, AdminHeader, useQueryParams } from '@/components/admin';
import { kindsOf } from '@/pages/AdminLogs';
import { Card, ErrorNote, Kicker, Page, Tag, trackStateTone } from '@/components/ui';
import { getTrack } from '@/lib/api';
import { metres, percent, relativeTime } from '@/lib/format';
import type { Track } from '@/lib/types';

const MUTED = 'var(--color-neutral-500)';

export default function AdminTrackHistory() {
  const { officialId = '' } = useParams();
  const [search, setParam] = useQueryParams();
  const [track, setTrack] = useState<Track | null | undefined>(undefined);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    let live = true;
    setTrack(undefined);
    setError(null);
    getTrack(officialId)
      .then((reply) => {
        if (live) setTrack(reply);
      })
      .catch((thrown: unknown) => {
        // A circuit the service has no row for still has a feed: a survey
        // that was refused, an issue about a layout nobody could name.
        if (live) {
          setTrack(null);
          setError(thrown);
        }
      });
    return () => {
      live = false;
    };
  }, [officialId]);

  const kinds = kindsOf(search);
  const name = track?.name || officialId;

  return (
    <Page max={1300} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <AdminHeader />

      <Card as="section" label="Circuit" style={{ gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 240 }}>
            <Kicker>circuit history</Kicker>
            <h2 style={{ fontSize: 24, margin: '4px 0 0' }}>{name}</h2>
            <div className="tabular-nums" style={{ fontSize: 12.5, color: MUTED, marginTop: 4 }}>
              {officialId}
              {track ? (
                <>
                  {' · '}
                  <Tag tone={trackStateTone(track.status)}>{track.status}</Tag>
                  {' · '}
                  {metres(track.length_m)} · left {percent(track.coverage.left)} · right{' '}
                  {percent(track.coverage.right)} ·{' '}
                  {track.last_published
                    ? `published ${relativeTime(track.last_published)}`
                    : 'never published'}
                  {' · '}
                  {track.uploads} uploads from {track.contributors}{' '}
                  {track.contributors === 1 ? 'account' : 'accounts'}
                  {track.held > 0 ? ` · ${track.held} held` : ''}
                  {track.pending > 0 ? ` · ${track.pending} pending` : ''}
                </>
              ) : null}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <Link to={`/tracks/${encodeURIComponent(officialId)}`} className="btn btn-secondary" style={{ fontSize: 12, textDecoration: 'none' }}>
              Its page
            </Link>
            <Link
              to={`/admin/uploads?official_id=${encodeURIComponent(officialId)}`}
              className="btn btn-secondary"
              style={{ fontSize: 12, textDecoration: 'none' }}
            >
              Uploads
            </Link>
            {track?.last_published ? (
              <Link
                to={`/admin/tracks/${encodeURIComponent(officialId)}/edit`}
                className="btn btn-primary"
                style={{ fontSize: 12, textDecoration: 'none' }}
              >
                Edit
              </Link>
            ) : null}
          </div>
        </div>
        {error && track === null ? (
          <ErrorNote compact error={error} title="the circuit itself could not be read; its history is below" />
        ) : null}
      </Card>

      <section aria-label="What happened to this circuit" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <ActivityFeed
          officialId={officialId}
          kinds={kinds}
          onKindsChange={(next) => setParam({ kind: next.length > 0 ? next.join(',') : null })}
        />
      </section>
    </Page>
  );
}
